import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set up global axios interceptor that ALWAYS reads latest token from localStorage
// This avoids race conditions between login state and API calls.
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('surya_token');
    if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// Global 403 interceptor — catch `service_not_authorized` responses from any
// endpoint and show a friendly Hindi toast so the user knows exactly which
// service the admin needs to grant.
let _lastServiceToastAt = 0;
axios.interceptors.response.use(
    (res) => res,
    (err) => {
        try {
            if (err && err.response && err.response.status === 403) {
                const d = err.response.data && err.response.data.detail;
                if (d && typeof d === 'object' && d.code === 'service_not_authorized') {
                    // Throttle to avoid toast spam if multiple parallel requests hit 403
                    const now = Date.now();
                    if (now - _lastServiceToastAt > 3000) {
                        _lastServiceToastAt = now;
                        toast.error(d.message_hi || d.message_en || 'Service access denied', {
                            description: d.label_hi ? `Service: ${d.label_hi}` : undefined,
                            duration: 6000,
                        });
                    }
                }
            }
        } catch { /* never break axios chain */ }
        return Promise.reject(err);
    }
);

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem('surya_token'));
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem('surya_user') || 'null'); } catch { return null; }
    });
    const [loading, setLoading] = useState(false);
    // Per-User Service Management state — populated after login (and refreshable)
    const [services, setServices] = useState(() => {
        try { return JSON.parse(localStorage.getItem('surya_services') || 'null'); } catch { return null; }
    });

    // Phase 47.24 — Midnight auto-logout. Har user ka session raat 12 baje
    // ke baad expire ho jaata hai — session ki `surya_login_date` (IST)
    // agar aaj se match nahi karti, hum logout kar dete hain. Ye har
    // 60 seconds pe check hota hai + tab wapas focus hone pe bhi.
    const _istToday = () => {
        try {
            return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
        } catch {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
    };

    const persist = (t, u) => {
        setToken(t); setUser(u);
        if (t) {
            localStorage.setItem('surya_token', t);
            localStorage.setItem('surya_login_date', _istToday());
        } else {
            localStorage.removeItem('surya_token');
            localStorage.removeItem('surya_login_date');
        }
        if (u) localStorage.setItem('surya_user', JSON.stringify(u)); else localStorage.removeItem('surya_user');
    };

    const persistServices = (s) => {
        setServices(s);
        if (s) localStorage.setItem('surya_services', JSON.stringify(s));
        else localStorage.removeItem('surya_services');
    };

    const fetchMyServices = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/me/services`);
            persistServices(data);
            return data;
        } catch {
            return null;
        }
    }, []);

    // Fetch /me/services whenever we have a token but no services cached
    // (e.g. right after page reload).
    useEffect(() => {
        if (token && !services) {
            fetchMyServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const hasService = useCallback((key) => {
        if (!services) return true;                 // don't hide before load — server still enforces
        if (services.is_admin_bypass) return true;
        return Array.isArray(services.granted) && services.granted.includes(key);
    }, [services]);

    const loginStep1 = useCallback(async (identifier, password) => {
        const { collectDeviceInfo } = await import('@/lib/deviceInfo');
        const device = await collectDeviceInfo();
        // Pull trusted-device token for this identifier (if any + not expired)
        const trustedKey = `td:${(identifier || '').trim().toLowerCase()}`;
        let trusted_device_token = null;
        try {
            const raw = localStorage.getItem(trustedKey);
            if (raw) {
                const p = JSON.parse(raw);
                if (p && p.token && p.expires_at && new Date(p.expires_at).getTime() > Date.now()) {
                    trusted_device_token = p.token;
                } else {
                    localStorage.removeItem(trustedKey);
                }
            }
        } catch { /* ignore */ }

        const { data } = await axios.post(`${API}/auth/login`, {
            identifier, password, device, trusted_device_token,
        });
        // Admin bypass OR trusted-device bypass — both return step='authenticated' + token
        if (data && data.step === 'authenticated' && data.token) {
            persist(data.token, data.user);
            try {
                const { data: svc } = await axios.get(`${API}/me/services`, {
                    headers: { Authorization: `Bearer ${data.token}` },
                });
                persistServices(svc);
            } catch { /* non-fatal */ }
        }
        return data;
    }, []);

    const loginStep2 = useCallback(async (identifier, otp, opts = {}) => {
        setLoading(true);
        try {
            const { collectDeviceInfo } = await import('@/lib/deviceInfo');
            const device = await collectDeviceInfo();
            const { data } = await axios.post(`${API}/auth/login/verify`, {
                identifier, otp, device,
                remember_device: !!opts.remember_device,
                tc_accepted:     !!opts.tc_accepted,
            });
            persist(data.token, data.user);
            // Persist trusted-device token to localStorage (identifier-scoped)
            if (data.trusted_device && data.trusted_device.token) {
                try {
                    const trustedKey = `td:${(identifier || '').trim().toLowerCase()}`;
                    localStorage.setItem(trustedKey, JSON.stringify({
                        token:      data.trusted_device.token,
                        expires_at: data.trusted_device.expires_at,
                    }));
                } catch { /* ignore */ }
            }
            try {
                const { data: svc } = await axios.get(`${API}/me/services`, {
                    headers: { Authorization: `Bearer ${data.token}` },
                });
                persistServices(svc);
            } catch { /* non-fatal */ }
            return { ...data.user, __signup_status: data.signup_status || 'active' };
        } finally { setLoading(false); }
    }, []);

    const loginResendOtp = useCallback(async (identifier) => {
        const { data } = await axios.post(`${API}/auth/login/resend-otp`, { identifier, password: '' });
        return data;
    }, []);

    const loginEmail = useCallback(async (email, password) => {
        setLoading(true);
        try {
            const { collectDeviceInfo } = await import('@/lib/deviceInfo');
            const device = await collectDeviceInfo();
            const { data } = await axios.post(`${API}/auth/login-email`, { email, password, device });
            persist(data.token, data.user);
            try {
                const { data: svc } = await axios.get(`${API}/me/services`, {
                    headers: { Authorization: `Bearer ${data.token}` },
                });
                persistServices(svc);
            } catch { /* non-fatal */ }
            return { ...data.user, __signup_status: data.signup_status || (data.user && data.user.signup_status) || 'active' };
        } finally { setLoading(false); }
    }, []);

    const loginMobile = useCallback(async (mobile, password) => {
        setLoading(true);
        try {
            const { collectDeviceInfo } = await import('@/lib/deviceInfo');
            const device = await collectDeviceInfo();
            const { data } = await axios.post(`${API}/auth/login-mobile`, { mobile, password, device });
            persist(data.token, data.user);
            try {
                const { data: svc } = await axios.get(`${API}/me/services`, {
                    headers: { Authorization: `Bearer ${data.token}` },
                });
                persistServices(svc);
            } catch { /* non-fatal */ }
            return { ...data.user, __signup_status: data.signup_status || (data.user && data.user.signup_status) || 'active' };
        } finally { setLoading(false); }
    }, []);

    const registerCustomer = useCallback(async (payload) => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/register`, payload);
            persist(data.token, data.user);
            return data.user;
        } finally { setLoading(false); }
    }, []);

    const requestOtp = useCallback(async (mobile) => {
        const { data } = await axios.post(`${API}/auth/request-otp`, { mobile });
        return data;
    }, []);

    const requestOtpEmail = useCallback(async (email) => {
        const { data } = await axios.post(`${API}/auth/request-otp-email`, { email });
        return data;
    }, []);

    const verifyOtp = useCallback(async (mobile, otp, name) => {
        setLoading(true);
        try {
            const { collectDeviceInfo } = await import('@/lib/deviceInfo');
            const device = await collectDeviceInfo();
            const { data } = await axios.post(`${API}/auth/verify-otp`, { mobile, otp, name, device });
            persist(data.token, data.user);
            return data.user;
        } finally { setLoading(false); }
    }, []);

    const verifyOtpEmail = useCallback(async (email, otp, name) => {
        setLoading(true);
        try {
            const { collectDeviceInfo } = await import('@/lib/deviceInfo');
            const device = await collectDeviceInfo();
            const { data } = await axios.post(`${API}/auth/verify-otp-email`, { email, otp, name, device });
            persist(data.token, data.user);
            return data.user;
        } finally { setLoading(false); }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('att_gate_last_check');
        localStorage.removeItem('surya_services');
        localStorage.removeItem('surya_login_date');
        setServices(null);
        // Best-effort: tell server to clear the httpOnly session cookie. Never blocks the local logout.
        try {
            axios.post(`${API}/auth/logout`).catch(() => { /* ignore */ });
        } catch { /* ignore */ }
        persist(null, null);
    }, []);

    // Phase 47.24 — Midnight auto-logout enforcement. Har user roz raat
    // 12 baje ke baad automatic logout ho jaata hai — agle din phir se
    // login karna padega. Check har 60 sec + jab tab wapas focus mile
    // (log-in ki gayi date se aaj IST match nahi kari to session end).
    useEffect(() => {
        if (!token) return;
        const check = () => {
            const savedDay = localStorage.getItem('surya_login_date');
            const today = _istToday();
            if (savedDay && savedDay !== today) {
                toast.info('Aapka session raat 12 baje reset ho gaya hai — kripya wapas login karein.');
                logout();
            }
        };
        // Run immediately in case the tab was left open overnight.
        check();
        const id = setInterval(check, 60 * 1000);
        window.addEventListener('focus', check);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', check);
        };
    }, [token, logout]);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`);
            persist(localStorage.getItem('surya_token'), data);
            return data;
        } catch { return null; }
    }, []);

    const updateProfile = useCallback(async (payload) => {
        const { data } = await axios.put(`${API}/auth/profile`, payload);
        persist(localStorage.getItem('surya_token'), data);
        return data;
    }, []);

    return (
        <AuthCtx.Provider value={{ token, user, loading, loginStep1, loginStep2, loginResendOtp, loginEmail, loginMobile, registerCustomer, requestOtp, verifyOtp, requestOtpEmail, verifyOtpEmail, logout, refreshMe, updateProfile,
            services, hasService, fetchMyServices }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}

export { API };
